import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { reverse } from 'named-urls';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { CircularProgress, IconButton } from '@mui/material';
import Box from '@mui/material/Box';

import DetailsLoader from 'ui/DetailsLoader';

import Record from 'ui/Record/Record';

import MainHeader from 'components/MainHeader';
import { CenteredCircularProgress } from 'components/Progress';
import { Button } from 'components/Button';

import NotFound from 'containers/Error/Pages/NotFound/NotFound';

import DataError from 'containers/Error/DataError';

import GreenCheck from 'components/Icons/GreenCheck';
import WhiteCheck from 'components/Icons/WhiteCheck';
import routes from 'core/routes';
import { isNotFound } from 'utils/api/errors';
import { TaskListResponse } from 'utils/api/modules/tasks/tasks.types';
import { useNavBarHeight } from 'utils/hooks/navbar';
import { useStore } from 'utils/hooks/store';

import { colors } from 'theme/colors';

import CustomModal from 'components/CustomModal';

import { TaskStatus } from 'utils/api/modules/tasks/tasks.constants';

import { ButtonWrapper, StyledPopover, MenuItem, MarkCompletedButton, CompletedButton, ModalText } from './styles';
import TaskProperties from './components/TaskProperties';
import { TaskActivitiesContent, TaskActivitiesFooter } from './components/TaskActivities';

interface ActionButtonProps {
  details?: TaskListResponse;
  creatingAudit: boolean;
  onStartAudit: () => void;
  onContinueAudit: () => void;
  onComplete: () => void;
}

const ActionButton = ({
  details,
  creatingAudit,
  onStartAudit,
  onContinueAudit,
  onComplete,
}: ActionButtonProps): React.ReactElement => {
  if (details?.template !== null) {
    if (details?.status === TaskStatus.Completed) {
      return <></>;
    }

    if (details?.status === TaskStatus.InProgress) {
      return (
        <Button
          size="large"
          color="primary"
          variant="outlined"
          startIcon={<ArrowForwardIcon />}
          onClick={onContinueAudit}
        >
          Continue audit
        </Button>
      );
    }

    return (
      <Button
        size="large"
        color="primary"
        variant="contained"
        disabled={creatingAudit}
        onClick={onStartAudit}
        startIcon={creatingAudit ? <CircularProgress size={20} /> : undefined}
      >
        Start audit
      </Button>
    );
  }

  if (details?.status === TaskStatus.Completed) {
    return (
      <CompletedButton size="large" color="success" variant="contained">
        <WhiteCheck />
        Completed
      </CompletedButton>
    );
  }

  return (
    <MarkCompletedButton onClick={onComplete} variant="outlined" size="large" color="success">
      <GreenCheck />
      Mark as Complete
    </MarkCompletedButton>
  );
};

const TaskDetails: React.FunctionComponent = (): React.ReactElement => {
  const { taskDetails, session } = useStore();
  const params = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const [deletionModalOpen, setDeletionModalOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const navBarHeight = useNavBarHeight();

  const [creatingAudit, setCreatingAudit] = React.useState(false);

  const handleClose = (): void => {
    if (!deleting) {
      setDeletionModalOpen(false);
    }
  };

  const onDelete = async (): Promise<void> => {
    setDeleting(true);
    const res = await taskDetails.deleteTask(taskDetails.details?.uuid as string);
    if (res) {
      navigate(-1);
    }
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const completeAction = async (): Promise<void> => {
    await taskDetails.updateTask({
      status: TaskStatus.Completed,
    } as unknown as Partial<TaskListResponse>);
  };

  const startAuditAction = async (): Promise<void> => {
    setCreatingAudit(true);
    try {
      await taskDetails.createAuditForm();
    } finally {
      setCreatingAudit(false);
    }
  };

  return (
    <DetailsLoader params={params} routeParam="taskId" store={taskDetails}>
      {({ isLoaded, error, reload }): React.ReactElement => {
        if (!isLoaded) {
          return <CenteredCircularProgress />;
        }

        if (error) {
          if (isNotFound(error)) {
            return <NotFound />;
          }
          return <DataError onReload={reload} />;
        }

        return (
          <Record
            Header={
              <MainHeader
                action={
                  session.user?.readOnly ? undefined : (
                    <Box sx={{ display: 'flex' }}>
                      <ActionButton
                        details={taskDetails.details}
                        creatingAudit={creatingAudit}
                        onStartAudit={startAuditAction}
                        onContinueAudit={() => taskDetails.continueAuditForm()}
                        onComplete={completeAction}
                      />
                      <ButtonWrapper>
                        <IconButton onClick={handleClick}>
                          <MoreVertIcon htmlColor={colors.text.secondary} />
                        </IconButton>
                      </ButtonWrapper>
                      <StyledPopover
                        id="popover-remove_action"
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={() => setAnchorEl(null)}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                      >
                        <MenuItem id="remove-button" onClick={() => setDeletionModalOpen(true)}>
                          Delete
                        </MenuItem>
                      </StyledPopover>
                    </Box>
                  )
                }
                title={taskDetails.details?.name as string}
                breadcrumbs={[
                  {
                    label: 'Actions',
                    link: routes.dashboard.tasks.list.toString(),
                  },
                  {
                    label: 'Tasks',
                    link: routes.dashboard.tasks.list.toString(),
                  },
                  {
                    label: 'Details',
                    link: reverse(routes.dashboard.tasks.details, { taskId: taskDetails.details?.uuid as string }),
                  },
                ]}
              />
            }
            disableMaxContentHeight
            Content={() => (
              <>
                <TaskProperties />

                <CustomModal
                  openModal={deletionModalOpen}
                  onClose={handleClose}
                  modalTitle="Delete Task"
                  modalWidth="xs"
                  cancelButton={
                    <Button
                      disabled={deleting}
                      onClick={handleClose}
                      autoFocus
                      id="cancel-button"
                      variant="outlined"
                      size="large"
                    >
                      Cancel
                    </Button>
                  }
                  submitButton={
                    <Button
                      onClick={onDelete}
                      disabled={deleting}
                      id="confirm-delete-button"
                      variant="contained"
                      size="large"
                      color="error"
                    >
                      Delete
                    </Button>
                  }
                >
                  <ModalText>Are you sure you want to delete this record?</ModalText>
                  <ModalText>Once removed, you will not be able to recover this data.</ModalText>
                </CustomModal>
              </>
            )}
            navBarHeight={navBarHeight}
            Activities={{
              Content: <TaskActivitiesContent />,
              Footer: <TaskActivitiesFooter />,
            }}
          />
        );
      }}
    </DetailsLoader>
  );
};

export default TaskDetails;
